/**
 * @license
 * Copyright Google LLC All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.dev/license
 */

import type { OnDestroy } from '@angular/core';
import { Directive, input } from '@angular/core';
import { NG_VALIDATORS, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MAT_INPUT_VALUE_ACCESSOR } from '@angular/material/input';
import type { MatTimepicker } from '@angular/material/timepicker';
import { MatTimepickerInput } from '@angular/material/timepicker';

interface WithCleanup {
  _cleanupClick?: () => void;
}

/**
 * Custom directive to override the behavior of `MatTimepickerInput`.
 * This prevents the timepicker from opening automatically (e.g. on click).
 */
@Directive({
  selector: 'input[appMindMatTimepicker]',
  exportAs: 'myMatTimepickerInput',
  host: {
    // Set accessibility and style attributes
    class: 'mat-timepicker-input',
    role: 'combobox',
    type: 'text',
    'aria-haspopup': 'listbox',
    '[attr.aria-activedescendant]': '_ariaActiveDescendant()',
    '[attr.aria-expanded]': '_ariaExpanded()',
    '[attr.aria-controls]': '_ariaControls()',
    '[attr.mat-timepicker-id]': 'timepicker()?.panelId',
    '[disabled]': 'disabled()',

    // Event handlers as in the original implementation
    '(blur)': '_handleBlur()',
    '(input)': '_handleInput($any($event.target).value)',
    '(keydown)': '_handleKeydown($event)',
  },
  providers: [
    // Provide form and Material value accessors as in the original
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: MindMatTimepickerInput,
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: MindMatTimepickerInput,
      multi: true,
    },
    {
      provide: MAT_INPUT_VALUE_ACCESSOR,
      useExisting: MindMatTimepickerInput,
    },
  ],
})
export class MindMatTimepickerInput<D> extends MatTimepickerInput<D> implements OnDestroy {
  override ngOnDestroy(): void {
    super.ngOnDestroy();
  }

  override readonly timepicker = input.required<MatTimepicker<D>>({
    alias: 'appMindMatTimepicker',
  });

  constructor() {
    super();
    (this as unknown as WithCleanup)._cleanupClick?.();
  }
}
